import { Docket, DocketStatus, KitchenDisplayMode } from '@oolio-group/domain';
import React, { useCallback, useMemo } from 'react';
import useSWR from 'swr';
import { DocketSource } from '../../../common/types';
import config from '../../../config';
import { useOrgTimings } from '../../../hooks/useOrgTimings';
import { useSession } from '../../../hooks/useSession';
import { useOrderNotification } from '../../../hooks/useOrderNotification';
import {
  openDocketslastUpdatedAt,
  tokenState,
  docketMoving,
  statsBarUpdated,
  docketsLastMovedAt,
  autoBumpFeatureVar,
} from '../../../state/apolloVars';
import { getDockets, saveDocketsOverwrite } from '../../../storage/docket';
import { clearSession } from '../../../utils/authFlowLink';
import { headers } from '../../../utils/fetchHeaders';
import { refetchTokenSerially } from '../../../utils/refetchTokenSerially';
import { getStartTimeandEndTime } from '../../../utils/timeHelper';
import * as settings from '../../../state/preferences';
import {
  POSTHOG_EVENT_ACTIONS,
  POSTHOG_EVENT_DATA,
  analyticsService,
} from '../../../analytics/AnalyticsService';
import { makeRequest } from '../../../utils/fetchHelper';
import {
  shouldAutoBumpDocket,
  getAutoBumpStatus,
} from '../../../utils/docketHelper';

export interface DocketHermesProps {
  docketStatus: DocketStatus;
  headers: headers;
}

export interface DocketResponse {
  dockets: Docket[];
  lastUpdatedAt: number;
  lastMovedAt?: number;
}
/*
The Purpose of this block is to do pooling of dockets at a fixed interval
*/

const DocketHermes: React.FC<DocketHermesProps> = ({
  docketStatus,
  headers,
}) => {
  let isRefreshing = false;
  const { session } = useSession();
  const { dateTime } = useOrgTimings();
  const timings = useMemo(
    () => getStartTimeandEndTime(dateTime?.startTime || ''),
    [dateTime?.startTime],
  );
  const { sendReceiveSmsNotification } = useOrderNotification();
  const { notifyOnReceive } = session.kitchenDisplay?.notificationConfig || {};

  const printerProfiles = (session?.kitchenDisplay?.printerProfiles || [])?.map(
    x => x && x.id,
  );

  const storeId = session?.currentStore?.id;
  const orgId = session?.currentOrganization?.id;

  const fetcher = useCallback(
    async (url: string) => {
      return fetch(url, {
        method: 'POST',
        body: JSON.stringify({
          ids: printerProfiles,
          status: docketStatus,
          ...(docketStatus == DocketStatus.COMPLETED && {
            startTime: timings.startTime,
            endTime: timings.endTime,
          }),
          ...(storeId && {
            storeId,
          }),
          ...(orgId && { orgId }),
        }),
        headers: headers,
      }).then(async r => {
        if (r.ok) return r.json();
        throw new Error(await r.text());
      });
    },
    [
      docketStatus,
      headers,
      printerProfiles,
      storeId,
      timings.endTime,
      timings.startTime,
      orgId,
    ],
  );
  useSWR(config.KDS_SERVICE_URI + '/dockets', fetcher, {
    refreshInterval: 10 * 1000, // 10 seconds polling
    dedupingInterval: 10 * 1000, // 10 seconds polling
    async onSuccess(docketsData) {
      const docketResponseData = docketsData as DocketResponse;

      const dockets = docketResponseData.dockets;
      const existingDockets = await getDockets(docketStatus); // get dockets from local storage

      if (
        docketStatus === DocketStatus.CREATED &&
        notifyOnReceive &&
        dockets?.length
      ) {
        const unNotifiedDockets = dockets.filter(
          d => !d?.notificationStatus?.smsOnReceive,
        );
        if (unNotifiedDockets?.length) {
          sendReceiveSmsNotification(unNotifiedDockets.map(item => item.id));
        }
      }

      // Auto-bump check based on device type (only for active dockets)
      if (docketStatus === DocketStatus.CREATED && dockets?.length) {
        const mode = session?.kitchenDisplay?.mode || KitchenDisplayMode.PREP;
        const rebumpToggle = session?.rebumpToggle || false;

        // Collect all docket IDs that need auto-bumping
        const docketIdsToAutoBump = dockets
          .filter(docket =>
            shouldAutoBumpDocket(
              docket,
              mode,
              rebumpToggle,
              autoBumpFeatureVar(),
            ),
          )
          .map(docket => docket.id);

        // Batch bump all dockets in a single API call
        if (docketIdsToAutoBump.length > 0) {
          try {
            await makeRequest(config.KDS_SERVICE_URI + '/docket/bump', {
              ids: docketIdsToAutoBump,
              status: getAutoBumpStatus(rebumpToggle),
            });
          } catch (error) {
            console.error(
              'Auto-bump failed for dockets',
              docketIdsToAutoBump,
              error,
            );
          }
        }
      }
      if (!docketMoving()) {
        if (docketStatus) {
          await saveDocketsOverwrite(
            dockets || [],
            docketStatus,
            DocketSource.POOLING,
          );
        }
        const isDocketsUpdated = dockets.length !== existingDockets.length;

        if (isDocketsUpdated) {
          openDocketslastUpdatedAt(performance.now());
          statsBarUpdated({ lastUpdatedAt: Date.now() });
          return;
        }

        if (!isNaN(docketResponseData?.lastUpdatedAt)) {
          openDocketslastUpdatedAt(docketResponseData?.lastUpdatedAt);
          if (
            docketResponseData?.lastUpdatedAt !==
            statsBarUpdated().lastUpdatedAt
          ) {
            statsBarUpdated({
              lastUpdatedAt: docketResponseData?.lastUpdatedAt,
            });
          }
        }

        if (
          docketResponseData?.lastMovedAt &&
          !isNaN(docketResponseData?.lastMovedAt) &&
          docketResponseData.lastMovedAt !== docketsLastMovedAt()
        ) {
          docketsLastMovedAt(docketResponseData.lastMovedAt);
        }
      }
    },
    onError() {
      if ((tokenState().expiredDate as number) < Date.now() && !isRefreshing) {
        isRefreshing = true;
        refetchTokenSerially()
          .catch(error => {
            // logout user
            console.error('FAIL TO REFRESH USER TOKEN', error);
            clearSession();
            tokenState({});
          })
          .finally(async () => {
            isRefreshing = false;
            const session = await settings.getSession();
            analyticsService.capture(
              POSTHOG_EVENT_ACTIONS.TOKEN_REFRESH_OR_EXPIRED,
              {
                orgId: session?.currentOrganization?.id,
                orgName: session?.currentOrganization?.name,
              } as POSTHOG_EVENT_DATA,
            );
          });
      }
    },
  });

  return <></>;
};

export default DocketHermes;
